// DEPENDENCIES ---------------------------------------------------------------- //

import { useEffect, useRef, useState } from 'react';

import { css } from '@emotion/css';
import { MdExpandMore } from 'react-icons/md';
import { Collapse, IconButton, LinearProgress, Paper, Theme, Typography, useTheme } from '@mui/material';

import { useProgramCoverageQuote } from '@features/moveCreation/hooks/useProgramCoverageQuote';
import { buildProgramCoverageLines, summarizeProgramCoverage } from '@features/moveCreation/lib/programCoverageLines';
import { eligibilityView, vehicleFactsPrompt } from '@features/moveCreation/lib/programVehicleEligibility';
import { EligibilityStatusLine } from '@features/moveCreation/components/ProgramEligibilityParts';
import { CoverageDetails, CoverageHeadline } from '@features/moveCreation/components/ProgramCoverageParts';

// COMPONENT ---------------------------------------------------------------- //

/**
 * Display only. An unmapped key shows as itself rather than disappearing,
 * because a product nobody labelled is exactly the case where the dealer most
 * needs to see what the system thinks they hold.
 */
const PRODUCT_LABELS: Record<string, string> = {
  'valet-full-service': 'Valet Full Service',
  'valet-lite': 'Valet Lite',
};

const productLabel = (key: string): string => PRODUCT_LABELS[key] ?? key;

/**
 * Live "Program Coverage" estimate in the move planner, rendered as a compact
 * bar PINNED to the top of the planner so it stays visible while the dealer
 * scrolls down to enter vehicle info. No figure shows until the vehicle facts
 * the program's eligibility reads are in, since the VIN decode can flip what
 * the dealer pays. After that the quote re-runs (debounced) as the vehicle
 * changes, the headline figure briefly highlights when it changes, and the
 * eligibility verdict shows under it. Tap the bar to expand the money
 * breakdown and every rule. The summary at the end of the form shows the same
 * figure, verdict and breakdown through the same parts.
 *
 * Renders nothing unless the customer's `program_visuals` flag is on and
 * they're enrolled (quote non-null → lines non-null). Informational only.
 */
const MovePlannerProgramCoverage = () => {
  const theme = useTheme();
  const cls = useStyles(theme);
  const clsx = useSxStyles(theme);

  const {
    quote,
    loading,
    visuals,
    productAmbiguous,
    candidateProductKeys,
    vehicleReady,
    missingVehicleFacts,
    vehicle,
    vehicleLabel,
    distanceMiles,
  } = useProgramCoverageQuote();
  const lines = buildProgramCoverageLines(quote, visuals);

  const [expanded, setExpanded] = useState(false);

  // Pulse the headline when the covered figure changes (a coverage flip should
  // be obvious). Only meaningful once the vehicle is in.
  const summary = vehicleReady && lines ? summarizeProgramCoverage(lines) : null;
  const [pulse, setPulse] = useState(false);
  const prevValue = useRef<number | null>(null);
  useEffect(() => {
    if (summary == null) {
      prevValue.current = null;
      return;
    }
    if (prevValue.current !== null && prevValue.current !== summary.value) {
      setPulse(true);
      const t = setTimeout(() => setPulse(false), 900);
      prevValue.current = summary.value;
      return () => clearTimeout(t);
    }
    prevValue.current = summary.value;
  }, [summary?.value]);

  // Nothing to show until enrolled + a lane is picked (lines null); show a thin
  // loader only while the flag is on and a quote is mid-flight.
  if (!lines) {
    // The rooftop is entitled to both Valet tiers and nothing says which one
    // this move is, so there is no honest coverage figure. Name the two
    // products rather than rendering nothing beside a lane strip that is still
    // quoting a price.
    if (visuals.active === true && productAmbiguous) {
      return (
        <div className={cls.sticky} data-testid='program-coverage-unresolved'>
          <Paper variant='custom' sx={{ padding: `10px 16px` }}>
            <Typography sx={{ fontWeight: 600, fontSize: `14px` }}>Program Coverage</Typography>
            <Typography sx={{ mt: 0.5, fontSize: `12px`, color: theme?.palette?.text?.secondary }}>
              This rooftop is set up for {candidateProductKeys.map(productLabel).join(' and ')}. Coverage
              differs between them, so we can't estimate it until the move says which one it is.
            </Typography>
          </Paper>
        </div>
      );
    }
    if (visuals.active === true && loading) {
      return (
        <div className={cls.sticky}>
          <Paper variant='custom' sx={{ padding: `10px 16px` }}>
            <LinearProgress />
          </Paper>
        </div>
      );
    }
    return null;
  }

  // Enrolled + lane, but the eligibility still assumes part of the vehicle.
  // Any figure now could change once the VIN decodes (an assumed-eligible make
  // reads as phantom coverage), so show none and say what's missing.
  if (!vehicleReady) {
    return (
      <div className={cls.sticky} data-testid='program-coverage-card'>
        <Paper variant='custom' sx={{ padding: `10px 16px` }}>
          <Typography sx={{ fontWeight: 600, fontSize: `14px` }}>Program Coverage</Typography>
          <Typography sx={{ mt: 0.5, fontSize: `12px`, color: theme?.palette?.text?.secondary }}>
            {vehicleFactsPrompt(missingVehicleFacts)}
          </Typography>
        </Paper>
      </div>
    );
  }

  const eligibility = quote ? eligibilityView(quote, vehicle, missingVehicleFacts) : null;

  return (
    <div className={cls.sticky} data-testid='program-coverage-card'>
      <Paper variant='custom' sx={{ padding: 0, overflow: 'hidden', ...(pulse ? clsx.pulse : null) }}>
        {/* Compact bar — click to expand */}
        <div
          className={cls.bar}
          onClick={() => setExpanded(v => !v)}
          role='button'
          aria-expanded={expanded}
          aria-label='Toggle program coverage details'
        >
          <Typography sx={{ fontWeight: 600, fontSize: `14px`, whiteSpace: 'nowrap' }}>
            Program Coverage
          </Typography>
          <div className={cls.summary}>
            <CoverageHeadline lines={lines} />
          </div>
          <IconButton size='small' sx={{ transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }}>
            <MdExpandMore size={20} />
          </IconButton>
        </div>

        {/* Outside the collapse: a dealer paying the ineligible figure has to see why. It
            judges the current entry, so it waits for the quote that judged it. */}
        {!loading && eligibility ? (
          <div className={cls.eligibility} data-testid='program-coverage-eligibility'>
            <EligibilityStatusLine view={eligibility} />
          </div>
        ) : null}

        <Collapse in={expanded} unmountOnExit>
          <div className={cls.detail}>
            <CoverageDetails
              lines={lines}
              eligibility={!loading ? eligibility : null}
              vehicleLabel={vehicleLabel}
              distanceMiles={distanceMiles}
            />
          </div>
        </Collapse>
      </Paper>
    </div>
  );
};

// STYLES ---------------------------------------------------------------- //

const useStyles = (_theme?: Theme) => ({
  // Pinned directly UNDER the app header (the sticky Header is 64px tall in the
  // Layout's scroll container), full-bleed so it spans the screen when stuck.
  // Mounted outside the planner's maxWidth container so it isn't width-clamped.
  sticky: css`
    position: sticky;
    top: 64px;
    z-index: 4;
    margin-bottom: 16px;
  `,
  bar: css`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    cursor: pointer;
  `,
  summary: css`
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-left: auto;
  `,
  detail: css`
    padding: 0 16px 16px;
  `,
  eligibility: css`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px 8px;
    padding: 0 16px 10px;
  `,
});

const useSxStyles = (theme?: Theme) => ({
  pulse: {
    animation: 'programCoveragePulse 900ms ease-out',
    '@keyframes programCoveragePulse': {
      '0%': { boxShadow: `0 0 0 0 ${theme?.palette?.primary?.main}55` },
      '100%': { boxShadow: `0 0 0 6px transparent` },
    },
  },
});

// EXPORT ---------------------------------------------------------------- //

export default MovePlannerProgramCoverage;
